import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { LoyaltyBrandAPIPromiseClient } from "../../../gen/witbooking/backoffice/loyalty/brand/v1/loyalty_brand_api_grpc_web_pb";
import {
  GetLoyaltyBrandByTickerRequest,
  LoyaltyBrand,
} from "../../../gen/witbooking/backoffice/loyalty/brand/v1/loyalty_brand_api_pb";
import { ACCESS_DENIED } from "../../../lib/auth";
import { useGrpcClient, usePropertyGrpcCall } from "../../../lib/grpc";
import { Option, OperationError } from "../types";

interface UseBrandDataProps {
  brandTickerOption: Option | null;
}

interface UseBrandDataReturn {
  selectedBrand?: LoyaltyBrand;
  loading: boolean;
  error?: OperationError;
  managedByOptions: Option[];
  usedByOptions: Option[];
}

export const useBrandData = ({ brandTickerOption }: UseBrandDataProps): UseBrandDataReturn => {
  const grpcClient = useGrpcClient(LoyaltyBrandAPIPromiseClient);
  const getLoyaltyBrandByTicker = usePropertyGrpcCall(
    grpcClient,
    grpcClient.getLoyaltyBrandByTicker
  );

  const { data, isLoading: loading, error: queryError } = useQuery({
    queryKey: ["loyaltyBrand", brandTickerOption?.value],
    queryFn: async () => {
      if (!brandTickerOption || !brandTickerOption.value) {
        return undefined;
      }

      try {
        const request = new GetLoyaltyBrandByTickerRequest();
        request.setBrandTicker(brandTickerOption.value);

        const response = await getLoyaltyBrandByTicker(request);
        return response.getBrand() || undefined;
      } catch (e) {
        if (e === ACCESS_DENIED) {
          throw new Error("Access denied");
        } else {
          const errorMessage = e instanceof Error ? e.message : "Error retrieving brand";
          throw new Error(`Error retrieving brand: ${errorMessage}`);
        }
      }
    },
    enabled: !!brandTickerOption?.value,
    staleTime: 1000 * 60 * 2, // 2 minutes
  });

  const managedByOptions = useMemo(
    () => (data ? data.getManagedByList().map(ticker => ({ value: ticker, label: ticker })) : []),
    [data]
  );

  const usedByOptions = useMemo(
    () => (data ? data.getUsedByList().map(ticker => ({ value: ticker, label: ticker })) : []),
    [data]
  );

  const error: OperationError | undefined = queryError
    ? { message: queryError.message }
    : undefined;

  return {
    selectedBrand: data,
    loading,
    error,
    managedByOptions,
    usedByOptions,
  };
};
